import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { loginRequest } from '../api/authApi';
import Logo from '../components/Logo';

// Dispatcher/Admin login — reuses the exact same login.php endpoint as
// the driver app. userType lets the person logging in specify which
// table to check (dispatcher or admin), since the same email could
// theoretically exist in both tables otherwise.
function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState('dispatcher');

  const onSubmit = async (data) => {
    setLoginError('');
    setLoading(true);

    try {
      const result = await loginRequest(data.email, data.password, userType);

      if (result.success) {
        localStorage.setItem('token', result.token);
        localStorage.setItem('user', JSON.stringify(result.user));
        navigate('/dashboard');
      } else {
        setLoginError(result.message || t('login.invalidCredentials'));
      }
    } catch (err) {
      setLoginError(t('login.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <Logo size={56} variant="blue-bg" />
        </div>

        <h1 style={styles.title}>{t('login.title')}</h1>
        <p style={styles.subtitle}>{t('login.subtitle')}</p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <label style={styles.label}>Login as</label>
          <select value={userType} onChange={(e) => setUserType(e.target.value)} style={styles.input}>
            <option value="dispatcher">Dispatcher</option>
            <option value="admin">Admin</option>
          </select>

          <label style={styles.label}>{t('login.email')}</label>
          <input {...register('email', { required: true })} type="text" style={styles.input} />
          {errors.email && <p style={styles.errorText}>Required</p>}

          <label style={styles.label}>{t('login.password')}</label>
          <input {...register('password', { required: true })} type="password" style={styles.input} />
          {errors.password && <p style={styles.errorText}>Required</p>}

          {loginError && <p style={styles.errorText}>{loginError}</p>}

          <button type="submit" disabled={loading} style={styles.loginButton}>
            {loading ? '...' : t('login.loginButton')}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    backgroundColor: '#2563eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    width: 380,
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 32,
    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    color: '#111827',
    marginBottom: 6,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 13,
    color: '#9ca3af',
    marginBottom: 24,
    textAlign: 'center',
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
    marginTop: 14,
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  errorText: {
    color: '#dc2626',
    fontSize: 12,
    marginTop: 4,
  },
  loginButton: {
    width: '100%',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: 700,
    fontSize: 15,
    padding: '12px 0',
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    marginTop: 22,
  },
};

export default Login;